/**
 * LossReasonFormDialog - Modal de criação/edição de Motivo de Perda
 *
 * RELEASE: 2026-07-10 v1.0.0
 */
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { XCircle, Loader2 } from 'lucide-react';
import {
  useUpsertLossReason,
  type LossReasonEntry,
} from '@/hooks/useCrmLossReasons';

// Validação de entrada
const schema = z.object({
  name: z.string().trim().min(1, 'Informe um nome').max(100),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  color: z
    .string()
    .trim()
    .regex(/^#([0-9a-fA-F]{6})$/, 'Use cor no formato #RRGGBB')
    .optional()
    .or(z.literal('')),
  sort_order: z.number().int().min(0).max(9999).optional(),
  is_active: z.boolean().optional(),
});

interface LossReasonFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: LossReasonEntry | null;
  onSaved?: (id: string) => void;
}

export function LossReasonFormDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: LossReasonFormDialogProps) {
  const upsert = useUpsertLossReason();

  // Estado local do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#ef4444');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sempre que o modal reabre, sincroniza com a entrada de edição
  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setDescription(editing?.description ?? '');
      setColor(editing?.color ?? '#ef4444');
      setSortOrder(editing?.sort_order ?? 0);
      setIsActive(editing?.is_active ?? true);
      setErrors({});
    }
  }, [open, editing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      name,
      description,
      color,
      sort_order: sortOrder,
      is_active: isActive,
    });
    if (!parsed.success) {
      const fieldErr: Record<string, string> = {};
      parsed.error.errors.forEach((er) => {
        fieldErr[er.path[0] as string] = er.message;
      });
      setErrors(fieldErr);
      return;
    }

    const id = await upsert.mutateAsync({
      id: editing?.id ?? null,
      name: parsed.data.name,
      description: parsed.data.description || null,
      color: parsed.data.color || null,
      sort_order: parsed.data.sort_order ?? 0,
      is_active: parsed.data.is_active ?? true,
    });

    onSaved?.(id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>
                {editing ? 'Editar motivo de perda' : 'Novo motivo de perda'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Motivos usados ao marcar um negócio como perdido.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lr-name">Nome *</Label>
            <Input
              id="lr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Ex.: Preço acima do orçamento"
              autoFocus
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lr-desc">Descrição</Label>
            <Textarea
              id="lr-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Contexto ou orientação para os vendedores"
            />
            {errors.description && (
              <p className="text-xs text-destructive">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lr-color">Cor</Label>
              <div className="flex items-center gap-2">
                <input
                  id="lr-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded border border-input cursor-pointer bg-background"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#ef4444"
                />
              </div>
              {errors.color && (
                <p className="text-xs text-destructive">{errors.color}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lr-order">Ordem</Label>
              <Input
                id="lr-order"
                type="number"
                min={0}
                max={9999}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="lr-active" className="cursor-pointer">
                Ativo
              </Label>
              <p className="text-xs text-muted-foreground">
                Motivos inativos não aparecem na seleção ao marcar como perdido.
              </p>
            </div>
            <Switch id="lr-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {editing ? 'Salvar alterações' : 'Criar motivo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
